"use client";

import { ErrorCode } from "@calcom/features/auth/lib/ErrorCode";
import { APP_NAME, WEBAPP_URL } from "@calcom/lib/constants";
import { emailRegex } from "@calcom/lib/emailSchema";
import { getSafeRedirectUrl } from "@calcom/lib/getSafeRedirectUrl";
import { useCompatSearchParams } from "@calcom/lib/hooks/useCompatSearchParams";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import classNames from "@calcom/ui/classNames";
import { Alert } from "@calcom/ui/components/alert";
import { Button } from "@calcom/ui/components/button";
import { EmailField, PasswordField } from "@calcom/ui/components/form";
import { Icon } from "@calcom/ui/components/icon";
import { LastUsed, useLastUsed } from "@calcom/web/modules/auth/hooks/useLastUsed";
import AddToHomescreen from "@components/AddToHomescreen";
import BackupCode from "@components/auth/BackupCode";
import TwoFactor from "@components/auth/TwoFactor";
import { zodResolver } from "@hookform/resolvers/zod";
import type { inferSSRProps } from "@lib/types/inferSSRProps";
import type { getServerSideProps } from "@server/lib/auth/login/getServerSideProps";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { signIn } from "next-auth/react";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";
import { AuthSplitShell } from "~/auth/components/AuthSplitShell";
import { LegalFooter } from "~/legal/LegalFooter";

interface LoginValues {
  email: string;
  password: string;
  totpCode: string;
  backupCode: string;
  csrfToken: string;
}

export type PageProps = inferSSRProps<typeof getServerSideProps>;

export default function Login({
  csrfToken,
  isGoogleLoginEnabled,
  isOutlookLoginEnabled,
  totpEmail,
}: PageProps) {
  const searchParams = useCompatSearchParams();
  const { t } = useLocale();
  const router = useRouter();
  const formSchema = z
    .object({
      email: z
        .string()
        .min(1, `${t("error_required_field")}`)
        .regex(emailRegex, `${t("enter_valid_email")}`),
      ...(totpEmail ? {} : { password: z.string().min(1, `${t("error_required_field")}`) }),
    })
    .passthrough();
  const methods = useForm<LoginValues>({ resolver: zodResolver(formSchema) });
  const { register, formState } = methods;
  const [twoFactorRequired, setTwoFactorRequired] = useState(!!totpEmail || false);
  const [twoFactorLostAccess, setTwoFactorLostAccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lastUsed, setLastUsed] = useLastUsed();

  const errorMessages: { [key: string]: string } = {
    [ErrorCode.IncorrectEmailPassword]: t("incorrect_email_password"),
    [ErrorCode.IncorrectTwoFactorCode]: `${t("incorrect_2fa_code")} ${t("please_try_again")}`,
    [ErrorCode.InternalServerError]: `${t("something_went_wrong")} ${t("please_try_again_and_contact_us")}`,
    [ErrorCode.ThirdPartyIdentityProviderEnabled]: t("account_created_with_identity_provider"),
  };

  let callbackUrl = searchParams?.get("callbackUrl") || "";

  if (/"\//.test(callbackUrl)) callbackUrl = callbackUrl.substring(1);

  if (!/^https?:\/\//.test(callbackUrl)) {
    callbackUrl = `${WEBAPP_URL}/${callbackUrl}`;
  }

  const safeCallbackUrl = getSafeRedirectUrl(callbackUrl);

  callbackUrl = safeCallbackUrl || "";

  const onSubmit = async (values: LoginValues) => {
    setErrorMessage(null);
    const res = await signIn<"credentials">("credentials", {
      ...values,
      callbackUrl,
      redirect: false,
    });
    if (!res) setErrorMessage(errorMessages[ErrorCode.InternalServerError]);
    else if (!res.error) {
      setLastUsed("credentials");
      router.push(callbackUrl);
    } else if (res.error === ErrorCode.SecondFactorRequired) setTwoFactorRequired(true);
    else if (res.error === ErrorCode.IncorrectBackupCode) setErrorMessage(t("incorrect_backup_code"));
    else if (res.error === ErrorCode.MissingBackupCodes) setErrorMessage(t("missing_backup_codes"));
    else setErrorMessage(errorMessages[res.error] || t("something_went_wrong"));
  };

  const showSocialLogin = isGoogleLoginEnabled || isOutlookLoginEnabled;
  const showSignupLink =
    process.env.NEXT_PUBLIC_DISABLE_SIGNUP !== "true" && searchParams?.get("register") !== "false";

  const signupHref = callbackUrl
    ? `/signup?callbackUrl=${encodeURIComponent(callbackUrl)}`
    : "/signup";

  return (
    <>
      <AuthSplitShell>
        <div className="flex flex-col gap-2">
          <h1 className="font-cal text-[28px] leading-none" data-testid="login-heading">
            {twoFactorRequired ? t("2fa_required") : t("welcome_back")}
          </h1>
          <p className="font-medium text-base text-subtle leading-5" data-testid="login-subtitle">
            {twoFactorRequired
              ? t("2fa_code")
              : t("sign_in_to_cal_com", {
                  appName: APP_NAME,
                })}
          </p>
        </div>

        <FormProvider {...methods}>
          {!twoFactorRequired && showSocialLogin && (
            <div className="mt-8 flex flex-col gap-6">
              {isGoogleLoginEnabled && (
                <Button
                  color="primary"
                  disabled={formState.isSubmitting}
                  data-testid="google"
                  className="w-full justify-center rounded-md text-center"
                  CustomStartIcon={
                    // eslint-disable-next-line @next/next/no-img-element
                    <img className="mr-2 h-4 w-4" src="/google-icon-colored.svg" alt="" />
                  }
                  onClick={async (e) => {
                    e.preventDefault();
                    setLastUsed("google");
                    await signIn("google", {
                      callbackUrl,
                    });
                  }}>
                  {t("signin_with_google")}
                  {lastUsed === "google" ? <LastUsed /> : null}
                </Button>
              )}
              {isOutlookLoginEnabled && (
                <Button
                  color="secondary"
                  disabled={formState.isSubmitting}
                  data-testid="microsoft"
                  className="w-full justify-center rounded-md text-center"
                  CustomStartIcon={
                    // eslint-disable-next-line @next/next/no-img-element
                    <img className="text-subtle mr-2 h-4 w-4" src="/microsoft-logo.svg" alt="" />
                  }
                  onClick={async (e) => {
                    e.preventDefault();
                    setLastUsed("microsoft");
                    await signIn("azure-ad", {
                      callbackUrl,
                    });
                  }}>
                  {t("signin_with_microsoft")}
                  {lastUsed === "microsoft" ? <LastUsed /> : null}
                </Button>
              )}

              <div className="relative flex items-center">
                <div className="grow border-subtle border-t" />
                <span className="mx-2 shrink font-normal text-sm text-subtle leading-none">
                  {t("or").toLocaleLowerCase()}
                </span>
                <div className="grow border-subtle border-t" />
              </div>
            </div>
          )}

          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            noValidate
            data-testid="login-form"
            className={classNames("flex flex-col gap-4", showSocialLogin && !twoFactorRequired ? "" : "mt-8")}>
            <div>
              <input defaultValue={csrfToken || undefined} type="hidden" hidden {...register("csrfToken")} />
            </div>

            {!twoFactorRequired && (
              <>
                <EmailField
                  id="email"
                  label={t("email")}
                  defaultValue={totpEmail || (searchParams?.get("email") as string)}
                  placeholder="john@doe.com"
                  autoComplete="email"
                  {...register("email")}
                />
                {formState.errors.email && (
                  <p data-testid="field-error" className="text-error text-sm">
                    {formState.errors.email.message}
                  </p>
                )}

                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <label htmlFor="password" className="block text-sm font-medium text-default">
                      {t("password")}
                    </label>
                    <Link
                      href="/auth/forgot-password"
                      tabIndex={-1}
                      className="text-sm font-normal text-subtle hover:text-emphasis">
                      {t("forgot")}
                    </Link>
                  </div>
                  <PasswordField
                    id="password"
                    labelSrOnly
                    label={t("password")}
                    autoComplete="current-password"
                    {...register("password")}
                  />
                </div>
                {formState.errors.password && (
                  <p data-testid="field-error" className="text-error text-sm">
                    {formState.errors.password.message}
                  </p>
                )}
              </>
            )}

            {twoFactorRequired && (
              <div className="space-y-4">
                {!twoFactorLostAccess ? <TwoFactor center /> : <BackupCode center />}
              </div>
            )}

            {errorMessage && <Alert severity="error" title={errorMessage} />}

            <Button
              type="submit"
              color="primary"
              className="my-2 w-full justify-center"
              disabled={formState.isSubmitting}
              loading={formState.isSubmitting}>
              {twoFactorRequired ? t("submit") : t("continue")}
            </Button>
          </form>

          {twoFactorRequired && (
            <div className="mt-4 flex justify-start gap-4">
              {!totpEmail ? (
                <>
                  <Button
                    color="minimal"
                    onClick={() => {
                      if (twoFactorLostAccess) {
                        setTwoFactorLostAccess(false);
                        methods.setValue("backupCode", "");
                      } else {
                        setTwoFactorRequired(false);
                        methods.setValue("totpCode", "");
                      }
                      setErrorMessage(null);
                    }}>
                    <Icon name="arrow-left" className="mr-2 size-4" />
                    {t("go_back")}
                  </Button>
                  {!twoFactorLostAccess && (
                    <Button
                      color="minimal"
                      onClick={() => {
                        setTwoFactorLostAccess(true);
                        setErrorMessage(null);
                        methods.setValue("totpCode", "");
                      }}>
                      <Icon name="lock" className="mr-2 size-4" />
                      {t("lost_access")}
                    </Button>
                  )}
                </>
              ) : (
                <Button
                  color="minimal"
                  onClick={() => {
                    window.location.replace("/");
                  }}>
                  {t("cancel")}
                </Button>
              )}
            </div>
          )}
        </FormProvider>

        {!twoFactorRequired && (
          <div className="mt-10 flex h-full flex-col justify-end pb-6 text-xs">
            <div className="flex flex-col text-sm">
              {showSignupLink && (
                <div className="flex gap-1">
                  <p className="text-subtle">{t("dont_have_an_account")}</p>
                  <Link href={signupHref} className="text-emphasis hover:underline">
                    {t("create_account")}
                  </Link>
                </div>
              )}
              <div className="mt-3">
                <LegalFooter />
              </div>
            </div>
          </div>
        )}
      </AuthSplitShell>

      <AddToHomescreen />
    </>
  );
}
